import { useCallback, useEffect, useRef, useState } from 'react'
import { loadRemoteVisit, syncOperationsDirect, syncPendingMedia, syncPendingOperations } from './api'
import {
  coalesceOperations,
  findVisitBySlug,
  getConflicts,
  getPendingMedia,
  getPendingOperations,
  resolveStoredConflict,
  saveVisitLocally,
  storageHealthcheck,
} from './offline'
import { createVisit, normaliseVisit } from './model'
import {
  applyAcceptedRevisions,
  applyServerRecord,
  prepareVisitCommit,
  recordKey,
} from './operations'
import type { Conflict, RevisionOperation, SyncState, VisitSnapshot } from './types'

const INITIALISE_TIMEOUT_MS = 10_000

interface CloudFallback {
  visit: VisitSnapshot
  operations: RevisionOperation[]
}

class DurableStorageError extends Error {}

const cloudFallbackKey = (slug: string) => `climatise-audit-cloud-fallback:${slug}`
const cloudRecoveryKey = (slug: string) => `climatise-audit-recovery:${slug}`

function readCloudFallback(slug: string): CloudFallback | null {
  try {
    const stored = window.localStorage.getItem(cloudFallbackKey(slug))
    return stored ? JSON.parse(stored) as CloudFallback : null
  } catch {
    return null
  }
}

function writeCloudFallback(slug: string, value: CloudFallback) {
  const serialized = JSON.stringify(value)
  try {
    window.localStorage.setItem(cloudFallbackKey(slug), serialized)
    if (window.localStorage.getItem(cloudFallbackKey(slug)) !== serialized) {
      throw new DurableStorageError('The emergency browser backup could not be verified.')
    }
  } catch (error) {
    if (error instanceof DurableStorageError) throw error
    throw new DurableStorageError('The emergency browser backup could not be written.')
  }
}

function preserveRecoverySnapshot(
  slug: string,
  visit: VisitSnapshot,
  operations: RevisionOperation[],
  reason: string,
) {
  const key = cloudRecoveryKey(slug)
  let existing: Array<CloudFallback & { capturedAt: string; reason: string }> = []
  try {
    existing = JSON.parse(window.localStorage.getItem(key) ?? '[]') as typeof existing
    if (!Array.isArray(existing)) existing = []
  } catch {
    existing = []
  }
  const fingerprint = JSON.stringify({ visit, operations })
  const retained = existing.filter((entry) => JSON.stringify({
    visit: entry.visit,
    operations: entry.operations,
  }) !== fingerprint).slice(0, 4)
  const serialized = JSON.stringify([{
    visit,
    operations,
    capturedAt: new Date().toISOString(),
    reason,
  }, ...retained])
  try {
    window.localStorage.setItem(key, serialized)
    if (window.localStorage.getItem(key) !== serialized) {
      throw new DurableStorageError('The recovery snapshot could not be verified.')
    }
  } catch (error) {
    if (error instanceof DurableStorageError) throw error
    throw new DurableStorageError('The recovery snapshot could not be written.')
  }
}

function hasLocalOnlyRecords(local: VisitSnapshot, remote: VisitSnapshot) {
  const remoteObservationIds = new Set(remote.observations.map((item) => item.id))
  const remoteOpportunityIds = new Set(remote.opportunities.map((item) => item.id))
  const remoteEvidenceIds = new Set(remote.evidence.map((item) => item.id))
  return local.observations.some((item) => !remoteObservationIds.has(item.id))
    || local.opportunities.some((item) => !remoteOpportunityIds.has(item.id))
    || local.evidence.some((item) => !remoteEvidenceIds.has(item.id))
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timer = 0
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = window.setTimeout(() => reject(new Error('Local storage did not respond in time.')), timeoutMs)
  })
  try {
    return await Promise.race([promise, timeout])
  } finally {
    window.clearTimeout(timer)
  }
}

function currentRecord(
  visit: VisitSnapshot,
  operation: Conflict['operation'],
): { action: 'upsert' | 'delete'; payload: unknown } {
  if (operation.recordType === 'section') {
    if (operation.recordId === 'template') {
      return {
        action: 'upsert',
        payload: {
          templateId: visit.templateId,
          templateVersion: visit.templateVersion,
          templateSnapshot: visit.templateSnapshot,
        },
      }
    }
    return {
      action: 'upsert',
      payload: visit[operation.recordId as keyof VisitSnapshot],
    }
  }
  const collection = operation.recordType === 'observation'
    ? visit.observations
    : operation.recordType === 'opportunity'
      ? visit.opportunities
      : visit.evidence
  const record = collection.find((item) => item.id === operation.recordId)
  return record
    ? { action: 'upsert', payload: record }
    : { action: 'delete', payload: null }
}

interface AuditStore {
  visit: VisitSnapshot | null
  ready: boolean
  syncState: SyncState
  lastSyncedAt: string | null
  conflicts: Conflict[]
  pendingCount: number
  storageMode: 'device' | 'cloud' | 'unavailable'
  authExpired: boolean
  commit: (mutator: (visit: VisitSnapshot) => VisitSnapshot) => Promise<void>
  syncNow: () => Promise<void>
  initialise: (authenticated: boolean) => Promise<void>
  resolveConflict: (operationId: string, choice: 'local' | 'server') => Promise<void>
}

export function useAuditStore(slug: string): AuditStore {
  const [visit, setVisit] = useState<VisitSnapshot | null>(null)
  const [ready, setReady] = useState(false)
  const [syncState, setSyncState] = useState<SyncState>('saved_local')
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null)
  const [conflicts, setConflicts] = useState<Conflict[]>([])
  const [pendingCount, setPendingCount] = useState(0)
  const [storageMode, setStorageMode] = useState<'device' | 'cloud' | 'unavailable'>('device')
  const [authExpired, setAuthExpired] = useState(false)
  const [syncRequestVersion, setSyncRequestVersion] = useState(0)
  const visitRef = useRef<VisitSnapshot | null>(null)
  const writeChain = useRef(Promise.resolve())
  const writeGeneration = useRef(0)
  const syncInFlight = useRef(false)
  const storageModeRef = useRef<'device' | 'cloud' | 'unavailable'>('device')
  const cloudOperationsRef = useRef<RevisionOperation[]>([])

  const refreshPending = useCallback(async (current: VisitSnapshot) => {
    const [operations, media, storedConflicts] = await Promise.all([
      getPendingOperations(current.id),
      getPendingMedia(current.id),
      getConflicts(),
    ])
    const activeMedia = media.filter((item) => (
      current.evidence.some((evidence) => evidence.id === item.id && !evidence.deletedAt)
    ))
    setPendingCount(operations.length + activeMedia.length)
    setConflicts(storedConflicts.filter((item) => item.operation.visitId === current.id))
  }, [])

  const initialise = useCallback(async (authenticated: boolean) => {
    setReady(false)
    let deviceStorageHealthy = false
    try {
      deviceStorageHealthy = await withTimeout(storageHealthcheck(), INITIALISE_TIMEOUT_MS)
    } catch {
      deviceStorageHealthy = false
    }
    storageModeRef.current = deviceStorageHealthy ? 'device' : 'cloud'
    setStorageMode(deviceStorageHealthy ? 'device' : 'cloud')

    try {
      const fallback = deviceStorageHealthy ? null : readCloudFallback(slug)
      const local = deviceStorageHealthy
        ? await withTimeout(findVisitBySlug(slug), INITIALISE_TIMEOUT_MS)
        : fallback?.visit ?? null
      if (fallback) {
        cloudOperationsRef.current = fallback.operations ?? []
        setPendingCount(cloudOperationsRef.current.length)
      }
      let selected = local
      if (authenticated && slug !== 'demo' && navigator.onLine) {
        try {
          const remote = await withTimeout(loadRemoteVisit(slug), INITIALISE_TIMEOUT_MS)
          const pending = deviceStorageHealthy
            ? local ? await withTimeout(getPendingOperations(local.id), INITIALISE_TIMEOUT_MS) : []
            : cloudOperationsRef.current
          if (local && !pending.length && JSON.stringify(local) !== JSON.stringify(remote.visit)) {
            try {
              preserveRecoverySnapshot(slug, local, pending, 'Before loading a newer server copy')
            } catch {
              selected = local
              setStorageMode('unavailable')
              storageModeRef.current = 'unavailable'
              throw new Error('The existing device copy could not be backed up safely.')
            }
          }
          if (local && !pending.length && hasLocalOnlyRecords(local, remote.visit)) {
            selected = local
            storageModeRef.current = 'unavailable'
            setStorageMode('unavailable')
            setSyncState('storage_unavailable')
            throw new DurableStorageError('The server copy is missing records retained on this device.')
          }
          selected = local && pending.length ? local : remote.visit
        } catch (error) {
          if ((error as { status?: number }).status === 401) setAuthExpired(true)
          selected = local
        }
      }
      if (!selected && slug === 'demo') selected = createVisit('buildings', 'demo')
      if (selected) {
        selected = normaliseVisit(selected)
        const contributor = window.localStorage.getItem(`site-audit-contributor:${slug}`)
        if (contributor) selected = { ...selected, contributorName: contributor }
        visitRef.current = selected
        setVisit(selected)
        if (deviceStorageHealthy) {
          await withTimeout(saveVisitLocally(selected), INITIALISE_TIMEOUT_MS)
          await withTimeout(refreshPending(selected), INITIALISE_TIMEOUT_MS)
        } else {
          writeCloudFallback(slug, { visit: selected, operations: cloudOperationsRef.current })
        }
      } else if (!deviceStorageHealthy) {
        storageModeRef.current = 'unavailable'
        setStorageMode('unavailable')
      }
    } catch {
      storageModeRef.current = 'cloud'
      setStorageMode('cloud')
      const fallback = readCloudFallback(slug)
      let recovered = fallback?.visit ?? null
      cloudOperationsRef.current = fallback?.operations ?? []
      if (!recovered && authenticated && slug !== 'demo' && navigator.onLine) {
        try {
          recovered = (await loadRemoteVisit(slug)).visit
        } catch (remoteError) {
          if ((remoteError as { status?: number }).status === 401) setAuthExpired(true)
        }
      }
      if (!recovered && slug === 'demo') recovered = createVisit('buildings', 'demo')
      if (recovered) {
        const normalised = normaliseVisit(recovered)
        visitRef.current = normalised
        setVisit(normalised)
        setPendingCount(cloudOperationsRef.current.length)
        try {
          writeCloudFallback(slug, { visit: normalised, operations: cloudOperationsRef.current })
        } catch {
          storageModeRef.current = 'unavailable'
          setStorageMode('unavailable')
          setSyncState('storage_unavailable')
        }
      } else {
        storageModeRef.current = 'unavailable'
        setStorageMode('unavailable')
      }
    } finally {
      setReady(true)
    }
  }, [refreshPending, slug])

  const commit = useCallback(async (mutator: (current: VisitSnapshot) => VisitSnapshot) => {
    const generation = ++writeGeneration.current
    setSyncState('saving')
    writeChain.current = writeChain.current.catch(() => undefined).then(async () => {
      const current = visitRef.current
      if (!current) return
      const mutated = mutator(current)
      const { visit: next, operations } = prepareVisitCommit(current, mutated)
      try {
        if (storageModeRef.current === 'device') {
          await withTimeout(saveVisitLocally(next, operations), INITIALISE_TIMEOUT_MS)
        } else {
          cloudOperationsRef.current = coalesceOperations(cloudOperationsRef.current, operations)
          writeCloudFallback(slug, { visit: next, operations: cloudOperationsRef.current })
        }
        visitRef.current = next
        setVisit(next)
        if (storageModeRef.current === 'device') {
          await refreshPending(next)
        } else {
          setPendingCount(cloudOperationsRef.current.length)
        }
        if (generation === writeGeneration.current) {
          setSyncState(navigator.onLine ? 'saved_local' : 'offline')
        }
        setSyncRequestVersion((version) => version + 1)
      } catch {
        storageModeRef.current = 'cloud'
        setStorageMode('cloud')
        cloudOperationsRef.current = coalesceOperations(cloudOperationsRef.current, operations)
        try {
          writeCloudFallback(slug, { visit: next, operations: cloudOperationsRef.current })
        } catch {
          storageModeRef.current = 'unavailable'
          setStorageMode('unavailable')
          setSyncState('storage_unavailable')
          throw new Error('This edit was not applied because no durable browser storage was available.')
        }
        visitRef.current = next
        setVisit(next)
        setPendingCount(cloudOperationsRef.current.length)
        setSyncState(navigator.onLine ? 'saved_local' : 'offline')
        setSyncRequestVersion((version) => version + 1)
      }
    })
    return writeChain.current
  }, [refreshPending, slug])

  const syncNow = useCallback(async () => {
    const current = visitRef.current
    if (!current || syncInFlight.current || slug === 'demo') return
    if (!navigator.onLine) {
      setSyncState('offline')
      return
    }
    syncInFlight.current = true
    setSyncState('syncing')
    try {
      if (storageModeRef.current !== 'device') {
        const batch = cloudOperationsRef.current.slice(0, 100)
        if (batch.length) {
          const result = await syncOperationsDirect(batch)
          const acceptedIds = new Set(result.accepted.map((item) => item.operationId))
          const acceptedByRecord = new Map(result.accepted.map((item) => [
            `${item.recordType}:${item.recordId}`,
            item.revision,
          ]))
          const updated = applyAcceptedRevisions(visitRef.current ?? current, result.accepted)
          cloudOperationsRef.current = cloudOperationsRef.current
            .filter((operation) => !acceptedIds.has(operation.id))
            .map((operation) => {
              const revision = acceptedByRecord.get(`${operation.recordType}:${operation.recordId}`)
              return revision == null ? operation : { ...operation, expectedRevision: revision }
            })
          const cloudConflicts = result.conflicts.flatMap((item) => {
            const operation = batch.find((candidate) => candidate.id === item.operationId)
            return operation
              ? [{ operation, serverRecord: item.serverRecord, serverRevision: item.serverRevision }]
              : []
          })
          visitRef.current = updated
          setVisit(updated)
          setConflicts(cloudConflicts)
          setPendingCount(cloudOperationsRef.current.length)
          writeCloudFallback(slug, { visit: updated, operations: cloudOperationsRef.current })
          setSyncState(cloudConflicts.length ? 'conflict' : cloudOperationsRef.current.length ? 'saved_local' : 'synced')
          if (!cloudConflicts.length && !cloudOperationsRef.current.length) setLastSyncedAt(new Date().toISOString())
        } else {
          const refreshGeneration = writeGeneration.current
          const remote = await loadRemoteVisit(slug)
          await writeChain.current
          const latestLocal = visitRef.current ?? current
          if (writeGeneration.current !== refreshGeneration || cloudOperationsRef.current.length) {
            writeCloudFallback(slug, {
              visit: latestLocal,
              operations: cloudOperationsRef.current,
            })
            setPendingCount(cloudOperationsRef.current.length)
            setSyncState('saved_local')
            setSyncRequestVersion((version) => version + 1)
            return
          }
          const refreshed = normaliseVisit({
            ...remote.visit,
            contributorName: latestLocal.contributorName,
          })
          if (hasLocalOnlyRecords(latestLocal, refreshed)) {
            preserveRecoverySnapshot(slug, latestLocal, [], 'Server refresh was missing local records')
            storageModeRef.current = 'unavailable'
            setStorageMode('unavailable')
            setSyncState('storage_unavailable')
            return
          }
          if (JSON.stringify(latestLocal) !== JSON.stringify(refreshed)) {
            preserveRecoverySnapshot(slug, latestLocal, [], 'Before automatic server refresh')
          }
          visitRef.current = refreshed
          setVisit(refreshed)
          setConflicts([])
          writeCloudFallback(slug, { visit: refreshed, operations: [] })
          setSyncState('synced')
          setLastSyncedAt(new Date().toISOString())
        }
        return
      }
      const acceptRecords = async (records: Parameters<typeof applyAcceptedRevisions>[1]) => {
        writeChain.current = writeChain.current.catch(() => undefined).then(async () => {
          const updated = applyAcceptedRevisions(visitRef.current ?? current, records)
          await withTimeout(saveVisitLocally(updated), INITIALISE_TIMEOUT_MS)
          visitRef.current = updated
          setVisit(updated)
        })
        await writeChain.current
      }
      await syncPendingOperations(current.id, acceptRecords)
      const uploaded = await syncPendingMedia(current)
      if (uploaded.length) {
        await commit((snapshot) => ({
          ...snapshot,
          evidence: snapshot.evidence.map((item) => {
            const match = uploaded.find((entry) => entry.id === item.id)
            return match ? { ...item, localOnly: false, objectKey: match.objectKey } : item
          }),
        }))
        await syncPendingOperations(current.id, acceptRecords)
      }
      const latest = visitRef.current ?? current
      await writeChain.current
      let remaining = await getPendingOperations(latest.id)
      let currentConflicts = (await getConflicts()).filter((item) => item.operation.visitId === current.id)
      let remainingMedia = (await getPendingMedia(latest.id)).filter((item) => (
        latest.evidence.some((evidence) => evidence.id === item.id && !evidence.deletedAt)
      ))
      if (!currentConflicts.length && !remaining.length && !remainingMedia.length) {
        const remote = await loadRemoteVisit(slug)
        writeChain.current = writeChain.current.catch(() => undefined).then(async () => {
          const freshest = visitRef.current ?? latest
          remaining = await getPendingOperations(freshest.id)
          currentConflicts = (await getConflicts()).filter((item) => item.operation.visitId === freshest.id)
          remainingMedia = (await getPendingMedia(freshest.id)).filter((item) => (
            freshest.evidence.some((evidence) => evidence.id === item.id && !evidence.deletedAt)
          ))
          if (!currentConflicts.length && !remaining.length && !remainingMedia.length) {
            const refreshed = normaliseVisit({ ...remote.visit, contributorName: freshest.contributorName })
            if (hasLocalOnlyRecords(freshest, refreshed)) {
              preserveRecoverySnapshot(slug, freshest, [], 'Server refresh was missing local records')
              storageModeRef.current = 'unavailable'
              setStorageMode('unavailable')
              setSyncState('storage_unavailable')
              return
            }
            await withTimeout(saveVisitLocally(refreshed), INITIALISE_TIMEOUT_MS)
            visitRef.current = refreshed
            setVisit(refreshed)
          }
        })
        await writeChain.current
      }
      await refreshPending(visitRef.current ?? latest)
      const hasPending = remaining.length > 0 || remainingMedia.length > 0
      setSyncState(currentConflicts.length ? 'conflict' : hasPending ? 'saved_local' : 'synced')
      if (!currentConflicts.length && !hasPending) setLastSyncedAt(new Date().toISOString())
    } catch (error) {
      if ((error as { status?: number }).status === 401) setAuthExpired(true)
      if (error instanceof DurableStorageError) {
        storageModeRef.current = 'unavailable'
        setStorageMode('unavailable')
        setSyncState('storage_unavailable')
      } else {
        setSyncState(navigator.onLine ? 'saved_local' : 'offline')
      }
    } finally {
      syncInFlight.current = false
    }
  }, [commit, refreshPending, slug])

  const resolveConflict = useCallback(async (operationId: string, choice: 'local' | 'server') => {
    if (storageModeRef.current !== 'device') {
      const conflict = conflicts.find((item) => item.operation.id === operationId)
      const current = visitRef.current
      if (!conflict || !current) return
      if (choice === 'server') {
        const server = applyServerRecord(
          current,
          conflict.operation,
          conflict.serverRecord,
          conflict.serverRevision,
        )
        visitRef.current = server
        setVisit(server)
        cloudOperationsRef.current = cloudOperationsRef.current.filter((operation) => operation.id !== operationId)
      } else {
        cloudOperationsRef.current = cloudOperationsRef.current.map((operation) => (
          operation.id === operationId
            ? { ...operation, expectedRevision: conflict.serverRevision }
            : operation
        ))
      }
      setConflicts((items) => items.filter((item) => item.operation.id !== operationId))
      setPendingCount(cloudOperationsRef.current.length)
      writeCloudFallback(slug, {
        visit: visitRef.current ?? current,
        operations: cloudOperationsRef.current,
      })
      setSyncState('saved_local')
      return
    }
    const conflict = (await getConflicts()).find((item) => item.operation.id === operationId)
    if (!conflict) return
    if (choice === 'server') {
      const current = visitRef.current
      if (!current) return
      const server = applyServerRecord(
        current,
        conflict.operation,
        conflict.serverRecord,
        conflict.serverRevision,
      )
      await resolveStoredConflict(server, conflict, [], true)
      visitRef.current = server
      setVisit(server)
      await refreshPending(server)
      const remainingConflicts = (await getConflicts()).filter((item) => item.operation.visitId === current.id)
      const remainingOperations = await getPendingOperations(current.id)
      const remainingMedia = (await getPendingMedia(current.id)).filter((item) => (
        server.evidence.some((evidence) => evidence.id === item.id && !evidence.deletedAt)
      ))
      setSyncState(remainingConflicts.length ? 'conflict' : remainingOperations.length || remainingMedia.length ? 'saved_local' : 'synced')
      return
    }
    const current = visitRef.current
    if (!current) return
    const key = recordKey(conflict.operation.recordType, conflict.operation.recordId)
    const local = {
      ...current,
      recordRevisions: { ...current.recordRevisions, [key]: conflict.serverRevision },
      updatedAt: new Date().toISOString(),
    }
    const record = currentRecord(current, conflict.operation)
    const retry = {
      ...conflict.operation,
      id: crypto.randomUUID(),
      expectedRevision: conflict.serverRevision,
      createdAt: new Date().toISOString(),
      attempts: 0,
      action: record.action,
      payload: record.payload,
    }
    await resolveStoredConflict(local, conflict, [retry], false)
    visitRef.current = local
    setVisit(local)
    await refreshPending(local)
    setSyncState('saved_local')
  }, [conflicts, refreshPending, slug])

  useEffect(() => {
    const online = () => {
      setSyncState('saved_local')
      void syncNow()
    }
    const offline = () => setSyncState('offline')
    const resume = () => {
      if (!document.hidden && navigator.onLine) void syncNow()
    }
    const leaving = () => {
      if (navigator.onLine) void syncNow()
    }
    window.addEventListener('online', online)
    window.addEventListener('offline', offline)
    window.addEventListener('pageshow', resume)
    window.addEventListener('pagehide', leaving)
    document.addEventListener('visibilitychange', resume)
    return () => {
      window.removeEventListener('online', online)
      window.removeEventListener('offline', offline)
      window.removeEventListener('pageshow', resume)
      window.removeEventListener('pagehide', leaving)
      document.removeEventListener('visibilitychange', resume)
    }
  }, [syncNow])

  useEffect(() => {
    if (!visit || slug === 'demo' || syncState !== 'saved_local') return
    const timer = window.setTimeout(() => void syncNow(), 1200)
    return () => window.clearTimeout(timer)
  }, [slug, syncNow, syncRequestVersion, syncState, visit])

  useEffect(() => {
    if (!visit || slug === 'demo' || pendingCount === 0) return
    const timer = window.setInterval(() => {
      if (navigator.onLine) void syncNow()
    }, 30_000)
    return () => window.clearInterval(timer)
  }, [pendingCount, slug, syncNow, visit])

  return {
    visit,
    ready,
    syncState,
    lastSyncedAt,
    conflicts,
    pendingCount,
    storageMode,
    authExpired,
    commit,
    syncNow,
    initialise,
    resolveConflict,
  }
}
